import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Paintbrush,
  Hammer,
  Sofa,
} from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import SplitSection from "@/components/sections/SplitSection";
import ImageFeatureCard from "@/components/sections/ImageFeatureCard";
import AccordionGroup from "@/components/sections/AccordionGroup";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/cleaning-hero.jpg";
import aboutLifestyle from "@/assets/about-lifestyle.jpg";
import servicePrep from "@/assets/service-prep.jpg";
import paintingHero from "@/assets/painting-hero.jpg";
import interiorDesignHero from "@/assets/interior-design-hero.jpg";
import carpentryHero from "@/assets/carpentry-hero.jpg";
import ctaSell from "@/assets/cta-sell.jpg";
import beforeAfterImage from "@/assets/bg-interior-1.jpg";

const cleaningPaths = [
  {
    eyebrow: "Seller Support",
    title: "Cleaning Before Selling Your Home",
    description:
      "We focus on creating a polished, move-in-ready environment that helps your home stand out in listing photos, showings, and buyer impressions.",
    image: servicePrep,
    imageAlt: "Professional cleaning for a home being prepared for sale",
  },
  {
    eyebrow: "Home Care",
    title: "Recurring or One-Time Cleaning",
    description:
      "We provide ongoing cleaning or one-time deep cleaning services for homeowners who want a clean, comfortable, and well-maintained living space.",
    image: aboutLifestyle,
    imageAlt: "Comfortable and well-maintained home interior after professional cleaning",
  },
];

const cleaningBenefits = [
  "Feels brighter and more open",
  "Signals proper care and maintenance",
  "Creates a stronger first impression",
  "Improves comfort and livability",
];

const includedServices = [
  "Kitchens and appliances",
  "Bathrooms and fixtures",
  "Floors, baseboards, and surfaces",
  "Windows, sills, and high-touch areas",
  "Dust removal and deep cleaning throughout",
];

const beforeAfter = {
  before: [
    "Dull surfaces and visible buildup",
    "Lingering odors or dust",
    "A space that feels heavy and lived in",
  ],
  after: [
    "Bright, fresh, and polished appearance",
    "A home that feels cared for",
    "A space that feels inviting and comfortable",
  ],
};

const sellerBenefits = [
  "Performs better in listing photos",
  "Attracts more serious buyers",
  "Feels more valuable",
  "Supports stronger offers",
];

const prepServices = [
  {
    title: "Painting services to refresh and modernize",
    description:
      "A fresh coat of paint helps a home feel clean, current, and more market-ready.",
    href: "/painting",
    ctaLabel: "Explore Painting",
    image: paintingHero,
    imageAlt: "Painting services to refresh a home before sale",
    icon: Paintbrush,
  },
  {
    title: "Carpentry and repairs to eliminate issues",
    description:
      "Addressing visible wear and repair items helps remove distractions and strengthen buyer confidence.",
    href: "/carpentry",
    ctaLabel: "Explore Carpentry",
    image: carpentryHero,
    imageAlt: "Carpentry and repair services to improve home presentation",
    icon: Hammer,
  },
  {
    title: "Interior design guidance to improve presentation",
    description:
      "Thoughtful design direction helps a space photograph better and feel more complete in person.",
    href: "/interior-design",
    ctaLabel: "Explore Interior Design",
    image: interiorDesignHero,
    imageAlt: "Interior design guidance for a well-presented home",
    icon: Sofa,
  },
];

const expectations = [
  "Detailed and professional cleaning",
  "Focus on high-impact areas",
  "Reliable and consistent service",
  "A home that feels fresh, clean, and ready",
];

const faqItems = [
  {
    title: "Do I need professional cleaning before selling my home?",
    content:
      "Yes. A professionally cleaned home creates a stronger first impression and helps attract more serious buyers.",
  },
  {
    title: "Do you offer deep cleaning services?",
    content:
      "Yes. We provide deep cleaning for homes that need extra attention before selling or for general maintenance.",
  },
  {
    title: "Do you offer recurring cleaning?",
    content:
      "Yes. We offer both one-time and recurring cleaning services based on your needs.",
  },
  {
    title: "How long does a cleaning take?",
    content:
      "It depends on the size and condition of the home, but most cleanings are completed within a few hours.",
  },
];

const CleaningPage = () => {
  useEffect(() => {
    document.title = "Professional House Cleaning Services in Massachusetts | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Professional cleaning services to prepare your home for sale or maintain a clean living space. Hadassah Homes delivers results you can see and feel."
      );
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/cleaning";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Cleaning Services"
        title="Professional House Cleaning Services in Massachusetts"
        subtitle="A Cleaner Home Changes Everything"
        backgroundImage={heroImage}
      >
        <div className="mx-auto max-w-xl space-y-5 md:space-y-6">
          <p className="text-white text-lg md:text-xl leading-[1.75]">
            Whether your goal is to get your home market-ready or simply enjoy a cleaner home, we deliver results you can see and feel immediately.
          </p>
          <Button variant="gold" size="lg" asChild>
            <Link to="/consultation">
              Book a Consultation
            </Link>
          </Button>
        </div>
      </PageHero>

      <ContentSection>
        <SplitSection
          title="A Cleaner Home Changes Everything"
          description="A clean home does more than look good. It changes how a space feels, how it’s experienced, and how it’s valued."
          image={aboutLifestyle}
          imageAlt="Clean and comfortable Massachusetts home interior"
        >
          <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-[1.85]">
            <p>
              At Hadassah Homes, we provide professional house cleaning services across Massachusetts for homeowners preparing to sell and for clients who want a consistently clean, well-maintained space.
            </p>
            <p>
              Whether your goal is to get your home market-ready or simply enjoy a cleaner home, we deliver results you can see and feel immediately.
            </p>
          </div>
          <div className="pt-3">
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent hover:text-accent/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Book your cleaning or consultation today
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </SplitSection>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader
          title="Cleaning That Matches Your Needs"
          description="Not every home needs the same approach. We tailor our cleaning services based on your goals."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {cleaningPaths.map((item) => (
            <article
              key={item.title}
              className="group relative h-[320px] overflow-hidden border border-secondary-foreground/14 md:h-[360px]"
            >
              <img
                src={item.image}
                alt={item.imageAlt}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/45 to-transparent" />
              <div className="relative z-10 flex h-full items-end p-6 md:p-8">
                <div className="max-w-[28rem]">
                  <p className="mb-3 font-body text-[10px] uppercase tracking-[0.24em] text-white/70">
                    {item.eyebrow}
                  </p>
                  <h3 className="mb-3 font-headline text-[2rem] md:text-[2.2rem] leading-[1.08] text-white">
                    {item.title}
                  </h3>
                  <p className="text-white/90 text-base md:text-[1.02rem] leading-[1.75]">
                    {item.description}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
        <p className="max-w-3xl mx-auto pt-10 md:pt-12 text-center text-secondary-foreground/82 text-base md:text-lg leading-[1.8]">
          The goal is simple. A home that feels fresh, cared for, and easy to enjoy.
        </p>
      </ContentSection>

      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader
              title="Why Professional Cleaning Matters"
              align="left"
            />
            <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-[1.85]">
              <p>Buyers, guests, and even you notice more than you think.</p>
              <p>Dust, buildup, odors, and overlooked areas can quickly change how a home is perceived.</p>
              <p>Cleaning is not just about appearance. It shapes the entire experience of the home.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {cleaningBenefits.map((item, index) => (
              <div
                key={item}
                className={[
                  "border border-border/65 bg-card px-5 py-6 md:px-6 md:py-7 transition-shadow hover:shadow-md",
                  index % 2 === 1 ? "sm:translate-y-6" : "",
                ].join(" ")}
              >
                <p className="font-body text-[10px] uppercase tracking-[0.16em] text-accent mb-3">
                  Benefit {String(index + 1).padStart(2, "0")}
                </p>
                <p className="font-headline text-[1.35rem] md:text-[1.7rem] text-foreground leading-[1.2]">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <div className="grid grid-cols-1 lg:grid-cols-[0.4fr_0.6fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader
              title="What’s Included in Our Cleaning Services"
              align="left"
            />
            <div className="space-y-4 text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
              <p>We focus on the areas that have the biggest impact on how your home looks and feels.</p>
              <p>Every detail contributes to how your home is experienced the moment someone walks in.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {includedServices.map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 border-t border-secondary-foreground/12 pt-4 md:pt-5"
              >
                <Check className="mt-0.5 h-4 w-4 text-accent shrink-0" />
                <p className="text-secondary-foreground text-base md:text-lg leading-[1.75]">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader
          title="Before and After the Clean"
          description="Cleaning does not just improve how your home looks. It changes how it is perceived."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-border/60">
          <article className="border-b border-border/60 md:border-b-0 md:border-r md:border-r-accent/28 bg-card">
            <div className="relative aspect-[16/11] overflow-hidden">
              <img
                src={beforeAfterImage}
                alt="Home interior before a professional cleaning"
                className="absolute inset-0 h-full w-full object-cover brightness-[0.62] saturate-[0.72]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/40" />
              <div className="absolute left-5 top-5 z-10 border border-white/18 bg-black/58 px-3 py-1.5">
                <span className="font-body text-[10px] uppercase tracking-[0.18em] text-white">
                  Before
                </span>
              </div>
            </div>
            <div className="px-6 py-6 md:px-8 md:py-8">
              <div className="space-y-4 max-w-md">
                {beforeAfter.before.map((item) => (
                  <p key={item} className="text-muted-foreground text-base md:text-lg leading-[1.8]">
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </article>

          <article className="bg-card">
            <div className="relative aspect-[16/11] overflow-hidden">
              <img
                src={beforeAfterImage}
                alt="Home interior after a professional cleaning"
                className="absolute inset-0 h-full w-full object-cover brightness-[1.05] saturate-[1.04] contrast-[1.03]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/22 via-black/8 to-transparent" />
              <div className="absolute left-5 top-5 z-10 border border-accent/35 bg-black/34 px-3 py-1.5">
                <span className="font-body text-[10px] uppercase tracking-[0.18em] text-white">
                  After
                </span>
              </div>
            </div>
            <div className="px-6 py-6 md:px-8 md:py-8">
              <div className="space-y-4 max-w-md">
                {beforeAfter.after.map((item) => (
                  <p key={item} className="text-muted-foreground text-base md:text-lg leading-[1.8]">
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </article>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SplitSection
          title="Cleaning for Sellers: A Simple Way to Increase Value"
          description="If you are preparing to sell your home, cleaning becomes part of your strategy."
          image={ctaSell}
          imageAlt="Professionally cleaned home ready for listing and showings"
          reverse
        >
          <div className="space-y-4 text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
            <div className="mt-6 space-y-3">
              {sellerBenefits.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-[#C9A95C]" strokeWidth={2.5} />
                  <p className="leading-relaxed text-white/90">{item}</p>
                </div>
              ))}
            </div>
            <p className="pt-2">
              This is one of the easiest and most cost-effective ways to improve your home before it hits the market.
            </p>
            <p>
              You can combine this with our{" "}
              <Link to="/property-preparation" className="text-accent hover:text-accent/80 transition-colors">
                Property Preparation Services
              </Link>{" "}
              and{" "}
              <Link to="/interior-design" className="text-accent hover:text-accent/80 transition-colors">
                Interior Design
              </Link>{" "}
              to maximize your results.
            </p>
          </div>
          <div className="pt-3">
            <Link
              to="/sell-your-home"
              className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent hover:text-accent/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
            >
              Explore Sell Your Home
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </SplitSection>
      </ContentSection>

      <ContentSection>
        <SectionHeader
          title="Part of a Complete Preparation Strategy"
          description="Cleaning works best when paired with the right improvements."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {prepServices.map((item) => (
            <article
              key={item.title}
              className="border border-border/65 bg-card overflow-hidden"
            >
              <div className="aspect-[16/11] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.imageAlt}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="space-y-4 px-6 py-6 md:px-7 md:py-7">
                <div className="flex items-center gap-3 text-accent">
                  <item.icon className="h-4 w-4" />
                  <span className="font-body text-[10px] uppercase tracking-[0.16em]">
                    Related Service
                  </span>
                </div>
                <h3 className="font-headline text-2xl text-foreground leading-[1.2]">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-base leading-[1.75]">
                  {item.description}
                </p>
                <Link
                  to={item.href}
                  className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent hover:text-accent/80 transition-colors"
                >
                  {item.ctaLabel}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>
        <div className="max-w-3xl mx-auto pt-14 md:pt-16">
          <div className="w-16 h-px bg-accent/45 mx-auto mb-6" />
          <p className="text-center text-muted-foreground text-lg md:text-[1.18rem] tracking-[0.01em] leading-[1.85]">
            Together, these services help your home stand out and perform at its highest level.
          </p>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <div className="grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader
              title="What You Can Expect"
              align="left"
            />
            <p className="text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
              We do not just clean homes. We help create spaces that feel better to live in and perform better when it matters.
            </p>
          </div>

          <div className="space-y-7 md:space-y-8 border-l border-accent/22 pl-6 md:pl-8">
            {expectations.map((item, index) => (
              <div
                key={item}
                className={index < expectations.length - 1 ? "border-b border-secondary-foreground/10 pb-7 md:pb-8" : ""}
              >
                <p className="font-headline text-[1.35rem] md:text-[1.85rem] text-secondary-foreground leading-[1.28]">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader title="Frequently Asked Questions" />
        <div className="max-w-3xl mx-auto">
          <AccordionGroup items={faqItems} />
        </div>
      </ContentSection>

      <section className="relative overflow-hidden py-28 md:py-36">
        <img
          src={heroImage}
          alt="Clean and market-ready home interior"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/94 via-black/80 to-black/58" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent" />

        <div className="relative z-10 mx-auto max-w-content px-5 md:px-12">
          <div className="mx-auto max-w-[42rem] text-center">
            <h2 className="font-headline text-3xl sm:text-4xl md:text-[3.45rem] leading-[1.08] text-white mb-5">
              Let’s Get Your Home Clean and Ready
            </h2>
            <p className="text-white/90 text-base md:text-lg leading-[1.8] max-w-[36rem] mx-auto mb-12">
              Whether you are preparing to sell your home or simply want a cleaner, more comfortable space, we are here to help.
            </p>
            <Button variant="gold" size="lg" asChild>
              <Link to="/consultation">
                Book your cleaning or consultation today
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default CleaningPage;
